"use client";

import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  cn,
  initials,
  scopedEmployees,
  withAlpha,
  STATUS_META,
  type Employee,
  type Scope,
  type Status,
  type Team,
} from "@routecore/core";
import { RollingNumber, Tilt3D } from "@routecore/motion-fx";

/* ------------------------------- primitives ------------------------------- */

export const inputCls =
  "w-full rounded-xl border border-line bg-porcelain px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-faint transition-colors focus:border-accent focus:outline-none";

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-ink-soft">
        {label}
      </span>
      {children}
    </label>
  );
}

export function StyledSelect({
  value,
  onChange,
  children,
  className,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <div className={cn("relative", className)}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(inputCls, "cursor-pointer appearance-none pr-9", disabled && "opacity-60 cursor-not-allowed")}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-ink-faint" />
    </div>
  );
}

export function PrimaryButton({
  children,
  onClick,
  icon: Icon,
  type,
  className,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  icon?: LucideIcon;
  type?: "button" | "submit";
  className?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type={type ?? "button"}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-sm font-semibold whitespace-nowrap text-porcelain shadow-card transition-all hover:bg-accent-deep hover:shadow-lifted active:scale-[0.98]",
        disabled && "pointer-events-none opacity-40",
        className,
      )}
    >
      {Icon && <Icon className="size-4" />}
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  onClick,
  icon: Icon,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  icon?: LucideIcon;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border border-line bg-porcelain px-4 py-2.5 text-sm font-semibold whitespace-nowrap text-ink transition-all hover:border-ink/30 active:scale-[0.98]",
        className,
      )}
    >
      {Icon && <Icon className="size-4" />}
      {children}
    </button>
  );
}

export function IconBtn({
  children,
  onClick,
  title,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  title?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        "grid size-8 place-items-center rounded-full text-ink-soft transition-colors hover:bg-cream hover:text-ink active:scale-95",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function ConfirmDelete({ onConfirm }: { onConfirm: () => void }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 2600);
    return () => clearTimeout(t);
  }, [armed]);

  if (armed) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setArmed(false);
          onConfirm();
        }}
        className="rounded-md bg-[#B4443C] px-2 py-1 text-[10px] font-bold tracking-wide text-white uppercase shadow-sm"
      >
        Sure?
      </button>
    );
  }
  return (
    <button
      type="button"
      title="Delete"
      onClick={(e) => {
        e.stopPropagation();
        setArmed(true);
      }}
      className="grid size-7 place-items-center rounded-md text-ink-faint transition-colors hover:bg-[#B4443C]/10 hover:text-[#B4443C]"
    >
      <Trash2 className="size-3.5" />
    </button>
  );
}

/* ------------------------------ status + tags ----------------------------- */

export function StatusPill({ status, small }: { status: Status; small?: boolean }) {
  const meta = STATUS_META[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap",
        small ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]",
      )}
      style={{
        color: meta.color,
        backgroundColor: withAlpha(meta.color, 0.12),
        border: `1px solid ${withAlpha(meta.color, 0.3)}`,
      }}
    >
      <span className="size-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
      {meta.label}
    </span>
  );
}

export function EmployeeDot({
  name,
  color,
  size = 30,
}: {
  name: string;
  color: string;
  size?: number;
}) {
  return (
    <span
      className="grid shrink-0 place-items-center rounded-full font-bold"
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, size * 0.34),
        color,
        backgroundColor: withAlpha(color, 0.14),
        border: `1.5px solid ${withAlpha(color, 0.45)}`,
      }}
    >
      {initials(name)}
    </span>
  );
}

/* ------------------------------ filter controls ---------------------------- */

export function ScopeFilter({
  employees,
  teams,
  scope,
  onChange,
  onAdd,
  onManageTeams,
}: {
  employees: Employee[];
  teams: Team[];
  scope: Scope;
  onChange: (s: Scope) => void;
  onAdd?: () => void;
  onManageTeams?: () => void;
}) {
  const [open, setOpen] = useState(false);

  // Close the popup on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const activeTeam = teams.find((t) => t.id === scope.team) ?? null;
  const visible = scopedEmployees(employees, scope);
  const count = (teamId: number) => employees.filter((e) => e.teamId === teamId).length;
  const unassigned = employees.filter((e) => e.teamId == null).length;

  const pickTeam = (team: number | "all") => {
    // Drop the employee drill-down when it no longer belongs to the new team.
    const stillValid =
      scope.employee !== "all" &&
      (team === "all" || employees.some((e) => e.id === scope.employee && e.teamId === team));
    onChange({ team, employee: stillValid ? scope.employee : "all" });
    setOpen(false);
  };

  return (
    <div className="-mb-1 flex items-center gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {/* team selector */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Filter by team"
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-all active:scale-[0.97]",
          activeTeam || open
            ? "border-ink bg-ink text-porcelain shadow-card"
            : "border-line bg-porcelain text-ink-soft hover:border-ink/30 hover:text-ink",
        )}
      >
        {activeTeam ? (
          <span
            className="size-2 rounded-full ring-2 ring-white/25"
            style={{ backgroundColor: activeTeam.color }}
          />
        ) : (
          <Users className="size-3.5" />
        )}
        {activeTeam ? activeTeam.name : "All team"}
        <ChevronDown className="size-3.5" />
      </button>

      <TeamPickerPopup
        open={open}
        onClose={() => setOpen(false)}
        teams={teams}
        employees={employees}
        scope={scope}
        onPick={pickTeam}
        onManageTeams={onManageTeams}
        memberCount={count}
        unassigned={unassigned}
      />

      <span className="mx-0.5 h-5 w-px shrink-0 bg-line" />

      {visible.map((e) => (
        <Chip
          key={e.id}
          active={scope.employee === e.id}
          onClick={() =>
            onChange({ ...scope, employee: scope.employee === e.id ? "all" : e.id })
          }
          label={e.name.split(" ")[0]}
          color={e.color}
        />
      ))}

      {visible.length === 0 && (
        <span className="shrink-0 px-1 text-xs font-semibold text-ink-faint italic">
          No members in this team yet
        </span>
      )}

      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-dashed border-ink-faint px-3 py-1.5 text-xs font-semibold text-ink-soft transition-colors hover:border-accent hover:text-accent"
        >
          <Plus className="size-3.5" />
          Add
        </button>
      )}
    </div>
  );
}

/** Centered popup listing every team as a large, easy-to-scan card. */
function TeamPickerPopup({
  open,
  onClose,
  teams,
  employees,
  scope,
  onPick,
  onManageTeams,
  memberCount,
  unassigned,
}: {
  open: boolean;
  onClose: () => void;
  teams: Team[];
  employees: Employee[];
  scope: Scope;
  onPick: (t: number | "all") => void;
  onManageTeams?: () => void;
  memberCount: (id: number) => number;
  unassigned: number;
}) {
  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[88] grid place-items-center overflow-y-auto p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            className="fixed inset-0 bg-ink/45 backdrop-blur-[3px]"
          />
          <motion.div
            initial={{ opacity: 0, y: 22, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.97 }}
            transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
            className="relative my-auto w-full max-w-lg overflow-hidden rounded-3xl border border-line bg-porcelain shadow-lifted"
          >
            {/* header */}
            <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
              <div>
                <div className="text-[10px] font-bold tracking-[0.22em] text-accent uppercase">
                  Scope
                </div>
                <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight">
                  Filter by team
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="grid size-9 shrink-0 place-items-center rounded-full border border-line text-ink-soft transition-colors hover:bg-cream hover:text-ink"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* options */}
            <div className="max-h-[52vh] overflow-y-auto px-4 pb-2">
              <button
                type="button"
                onClick={() => onPick("all")}
                className={cn(
                  "mb-2 flex w-full items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all active:scale-[0.99]",
                  scope.team === "all"
                    ? "border-ink bg-ink text-porcelain shadow-card"
                    : "border-line bg-paper/60 hover:border-ink/25 hover:bg-cream/60",
                )}
              >
                <span
                  className={cn(
                    "grid size-10 shrink-0 place-items-center rounded-xl",
                    scope.team === "all" ? "bg-white/10 text-porcelain" : "bg-ink/[0.07] text-ink-soft",
                  )}
                >
                  <Users className="size-4.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-bold">All teams</span>
                  <span
                    className={cn(
                      "block text-xs",
                      scope.team === "all" ? "text-porcelain/60" : "text-ink-faint",
                    )}
                  >
                    Everyone · {employees.length} {employees.length === 1 ? "member" : "members"}
                  </span>
                </span>
                {scope.team === "all" && <Check className="size-5 shrink-0" />}
              </button>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {teams.map((t) => {
                  const active = scope.team === t.id;
                  const n = memberCount(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => onPick(t.id)}
                      className={cn(
                        "flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all active:scale-[0.99]",
                        active ? "shadow-card" : "border-line bg-paper/60 hover:border-ink/25 hover:bg-cream/60",
                      )}
                      style={
                        active
                          ? {
                              borderColor: t.color,
                              backgroundColor: withAlpha(t.color, 0.1),
                            }
                          : undefined
                      }
                    >
                      <span
                        className="grid size-10 shrink-0 place-items-center rounded-xl"
                        style={{ backgroundColor: withAlpha(t.color, 0.16), color: t.color }}
                      >
                        <Users className="size-4.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-bold">{t.name}</span>
                        <span className="block text-xs text-ink-faint">
                          {n} {n === 1 ? "member" : "members"}
                        </span>
                      </span>
                      {active && (
                        <Check className="size-5 shrink-0" style={{ color: t.color }} />
                      )}
                    </button>
                  );
                })}
              </div>

              {teams.length === 0 && (
                <div className="grid place-items-center gap-2 rounded-2xl border border-dashed border-line py-10 text-center">
                  <Users className="size-5 text-ink-faint" />
                  <p className="text-sm text-ink-soft">No teams created yet.</p>
                </div>
              )}
            </div>

            {/* footer */}
            <div className="mt-2 flex items-center justify-between border-t border-line/70 bg-paper/60 px-6 py-3.5">
              <span className="text-[11px] font-semibold text-ink-faint">
                {unassigned > 0
                  ? `${unassigned} member${unassigned === 1 ? "" : "s"} unassigned`
                  : "All members assigned"}
              </span>
              {onManageTeams && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onManageTeams();
                  }}
                  className="text-xs font-bold text-accent transition-opacity hover:opacity-70"
                >
                  Manage teams
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function Chip({
  active,
  onClick,
  label,
  color,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  color?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-all active:scale-[0.97]",
        active
          ? "border-ink bg-ink text-porcelain shadow-card"
          : "border-line bg-porcelain text-ink-soft hover:border-ink/30 hover:text-ink",
      )}
    >
      {color && (
        <span
          className="size-2 rounded-full"
          style={{ backgroundColor: color, boxShadow: active ? "0 0 0 2px rgba(255,255,255,0.25)" : undefined }}
        />
      )}
      {label}
    </button>
  );
}

export function Segmented<T extends string>({
  id,
  options,
  value,
  onChange,
}: {
  id: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-full border border-line bg-porcelain p-1 shadow-sm">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "relative rounded-full px-3.5 py-1.5 text-xs font-bold tracking-wide transition-colors",
            value === o.value ? "text-porcelain" : "text-ink-soft hover:text-ink",
          )}
        >
          {value === o.value && (
            <motion.span
              layoutId={`seg-${id}`}
              className="absolute inset-0 rounded-full bg-ink"
              transition={{ type: "spring", bounce: 0.25, duration: 0.5 }}
            />
          )}
          <span className="relative z-10">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export function PeriodNav({
  label,
  onPrev,
  onNext,
  onReset,
}: {
  label: string;
  onPrev: () => void;
  onNext: () => void;
  onReset: () => void;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-full border border-line bg-porcelain p-1 shadow-sm">
      <IconBtn onClick={onPrev} title="Previous">
        <ChevronLeft className="size-4" />
      </IconBtn>
      <button
        type="button"
        onClick={onReset}
        title="Jump to today"
        className="px-2.5 text-sm font-bold whitespace-nowrap transition-colors hover:text-accent"
      >
        {label}
      </button>
      <IconBtn onClick={onNext} title="Next">
        <ChevronRight className="size-4" />
      </IconBtn>
    </div>
  );
}

/* --------------------------------- KPI card -------------------------------- */

export function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  accent = "#E4572E",
  delay = 0,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
  accent?: string;
  delay?: number;
}) {
  return (
    <Tilt3D radius="1rem" intensity={11} lift={-7}>
      <div className="relative overflow-hidden rounded-2xl border border-line bg-porcelain p-4 shadow-card">
        {/* depth wash */}
        <span
          aria-hidden
          className="pointer-events-none absolute -top-10 -right-10 size-28 rounded-full opacity-50 blur-2xl"
          style={{ backgroundColor: withAlpha(accent, 0.22) }}
        />
        <div className="relative flex items-center gap-2" style={{ transform: "translateZ(26px)" }}>
          <span
            className="grid size-7 place-items-center rounded-lg"
            style={{ backgroundColor: withAlpha(accent, 0.12), color: accent }}
          >
            <Icon className="size-3.5" />
          </span>
          <span className="text-[10px] font-bold tracking-[0.14em] text-ink-soft uppercase">
            {label}
          </span>
        </div>
        <div
          className="relative mt-3 font-display text-[28px] leading-none font-semibold tracking-tight"
          style={{ transform: "translateZ(42px)" }}
        >
          <RollingNumber value={value} delay={delay} />
        </div>
        {sub && (
          <div
            className="relative mt-1.5 truncate text-xs text-ink-soft"
            style={{ transform: "translateZ(18px)" }}
          >
            {sub}
          </div>
        )}
      </div>
    </Tilt3D>
  );
}
